// Pure SVG string builder for the piano visualizer.
// Used by preview export and batch export.

import { NOTE_NAMES_SHARP, WHITE_PCS } from "./music";

const WHITE_KEY_W = 60;
const WHITE_KEY_H = 260;
const BLACK_KEY_W = 36;
const BLACK_KEY_H = 160;

function getKeyLayout(startingPc, octaves) {
  const whiteKeys = [];
  const blackKeys = [];
  let whiteIdx = 0;
  const totalSemis = octaves * 12;
  for (let semi = 0; semi < totalSemis; semi++) {
    const pc = (startingPc + semi) % 12;
    if (WHITE_PCS.includes(pc)) {
      whiteKeys.push({ x: whiteIdx * WHITE_KEY_W, pc, semi });
      whiteIdx += 1;
    } else {
      blackKeys.push({
        x: whiteIdx * WHITE_KEY_W - BLACK_KEY_W / 2,
        pc,
        semi,
      });
    }
  }
  return { whiteKeys, blackKeys, totalWhite: whiteIdx };
}

export function buildPianoSVGString({
  octaves = 2,
  startingPc = 0,
  startingOctaveNumber = 4,
  highlightedPCs = null,
  highlightedOffsets = null,
  highlightColor = "#3B82F6",
  backgroundColor = "#FFFFFF",
  showLabels = true,
  title = "",
  noteNames = NOTE_NAMES_SHARP,
}) {
  const { whiteKeys, blackKeys, totalWhite } = getKeyLayout(
    startingPc,
    octaves
  );
  const width = totalWhite * WHITE_KEY_W;
  const height = WHITE_KEY_H + 40;

  const isHi = (semi, pc) => {
    if (highlightedOffsets) return highlightedOffsets.has(semi);
    if (highlightedPCs) return highlightedPCs.has(pc);
    return false;
  };

  const startingAbs = startingPc + 12 * startingOctaveNumber;
  const labelFor = (semi) => {
    const abs = startingAbs + semi;
    return {
      name: noteNames[((abs % 12) + 12) % 12],
      octave: Math.floor(abs / 12),
    };
  };

  const titleH = title ? 40 : 0;

  const parts = [];
  parts.push(`<?xml version="1.0" standalone="no"?>`);
  parts.push(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="-20 -${
      20 + titleH
    } ${width + 40} ${height + 40 + titleH}">`
  );
  parts.push(
    `<rect x="-20" y="-${20 + titleH}" width="${width + 40}" height="${
      height + 40 + titleH
    }" fill="${backgroundColor}"/>`
  );

  if (title) {
    parts.push(
      `<text x="0" y="-25" font-family="'Space Grotesk', sans-serif" font-size="28" font-weight="800" fill="#111827">${escapeXml(
        title
      )}</text>`
    );
  }

  // White rects
  for (const k of whiteKeys) {
    const hi = isHi(k.semi, k.pc);
    parts.push(
      `<rect x="${k.x}" y="0" width="${WHITE_KEY_W}" height="${WHITE_KEY_H}" fill="${
        hi ? highlightColor : "#FFFFFF"
      }" stroke="#111827" stroke-width="2"/>`
    );
  }
  // Black rects
  for (const k of blackKeys) {
    const hi = isHi(k.semi, k.pc);
    parts.push(
      `<rect x="${k.x}" y="0" width="${BLACK_KEY_W}" height="${BLACK_KEY_H}" fill="${
        hi ? highlightColor : "#111827"
      }" stroke="#111827" stroke-width="2"/>`
    );
  }
  // Labels
  if (showLabels) {
    for (const k of whiteKeys) {
      const hi = isHi(k.semi, k.pc);
      const { name, octave } = labelFor(k.semi);
      parts.push(
        `<text x="${k.x + WHITE_KEY_W / 2}" y="${
          WHITE_KEY_H - 18
        }" text-anchor="middle" font-family="'JetBrains Mono', monospace" font-size="16" font-weight="700" fill="${
          hi ? "#FFFFFF" : "#111827"
        }">${name}</text>`
      );
    }
    for (const k of blackKeys) {
      const hi = isHi(k.semi, k.pc);
      if (!hi) continue;
      const { name } = labelFor(k.semi);
      parts.push(
        `<text x="${k.x + BLACK_KEY_W / 2}" y="${
          BLACK_KEY_H - 12
        }" text-anchor="middle" font-family="'JetBrains Mono', monospace" font-size="12" font-weight="700" fill="#FFFFFF">${name}</text>`
      );
    }
  }

  parts.push(`</svg>`);
  return parts.join("");
}

function escapeXml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

export function downloadSVGString(svgString, filename) {
  const blob = new Blob([svgString], {
    type: "image/svg+xml;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
